import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Boxes, Plus, Search, Filter, SlidersHorizontal } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  EmptyState,
  StatusPill,
  Pagination,
  FilterBuilder,
  type ColumnDef,
  type StatusTone,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useClusters, useDeleteCluster, type K8sCluster } from '@/api/k8s';
import { RowMenu } from '../compute/components/CrudBits';
import { ListPageShell, TableSkeleton, ErrorState } from '../compute/components/ListPageShell';
import { formatDate, useDebounce } from '../shared';

const PAGE_SIZE = 20;

// Columns the user can toggle. Name + actions are always visible.
const TOGGLEABLE = [
  { id: 'version', label: 'Version' },
  { id: 'status', label: 'Status' },
  { id: 'pools', label: 'Node pools' },
  { id: 'created', label: 'Created' },
];

const DEFAULT_VISIBLE: Record<string, boolean> = {
  version: true,
  status: true,
  pools: true,
  created: true,
};

// Cluster lifecycle → StatusPill tone. Matches the status strings the
// backend returns (see CLUSTER_TRANSITIONING regex in clusters.ts).
const CLUSTER_STATUS_TONE: Record<string, StatusTone> = {
  running: 'success',
  active: 'success',
  ready: 'success',
  pending: 'warning',
  deploying: 'warning',
  creating: 'warning',
  provisioning: 'warning',
  updating: 'warning',
  error: 'danger',
  failed: 'danger',
};

function clusterTone(s?: string): StatusTone {
  const v = String(s ?? '').toLowerCase();
  return CLUSTER_STATUS_TONE[v] ?? (
    v.includes('run') || v.includes('active') || v.includes('ready') ? 'success' :
    v.includes('pend') || v.includes('deploy') || v.includes('creat') || v.includes('provision') ? 'warning' :
    v.includes('error') || v.includes('fail') ? 'danger' :
    'muted'
  );
}

// Derive the ordered set of distinct status values from live data — used for
// the Status filter multiselect so the list reflects what actually exists.
function deriveStatusOptions(clusters: K8sCluster[]) {
  const seen = new Set<string>();
  const opts: { value: string; label: string }[] = [];
  for (const c of clusters) {
    const v = (c.status || c.state || '').toLowerCase();
    if (v && !seen.has(v)) {
      seen.add(v);
      // Capitalize first letter for the label.
      opts.push({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) });
    }
  }
  return opts;
}

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

export function ClusterList() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: clusters = [], isLoading, isError, refetch } = useClusters(serviceId);

  const [del, setDel] = useState<K8sCluster | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [page, setPage] = useState(1);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);

  const base = `/services/kubernetes/${serviceId}/clusters`;

  const statusOptions = useMemo(() => deriveStatusOptions(clusters), [clusters]);

  const filterFields: FilterField[] = [
    {
      value: 'status',
      label: 'Status',
      input: 'multiselect',
      operators: [{ value: 'is_any_of', label: 'is any of' }],
      options: statusOptions,
    },
  ];

  // Collect active status values from the filter conditions.
  const activeStatuses = useMemo(() => {
    const vals = conditions
      .filter((c) => c.field === 'status')
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return new Set(vals);
  }, [conditions]);

  // Client-side filter: search by name/id, then by status conditions.
  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return clusters.filter((c) => {
      if (q && !(c.name || c.id).toLowerCase().includes(q)) return false;
      if (activeStatuses.size > 0) {
        const s = (c.status || c.state || '').toLowerCase();
        if (!activeStatuses.has(s)) return false;
      }
      return true;
    });
  }, [clusters, debouncedSearch, activeStatuses]);

  // Client-side pagination over the filtered set.
  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Reset to page 1 when filtering reduces results below current page.
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageStart = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(safePage * PAGE_SIZE, total);

  const remove = useDeleteCluster(serviceId!, {
    onSuccess: () => { show({ tone: 'success', title: 'Cluster deleting' }); setDel(null); },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns = useMemo<ColumnDef<K8sCluster, unknown>[]>(() => {
    const all: Record<string, ColumnDef<K8sCluster, unknown>> = {
      version: {
        id: 'version',
        header: 'Version',
        enableSorting: false,
        cell: ({ row }) => muted(row.original.version?.gitVersion || row.original.metadata?.version || '—'),
      },
      status: {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original.status || row.original.state || 'Unknown';
          return <StatusPill tone={clusterTone(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</StatusPill>;
        },
      },
      pools: {
        id: 'pools',
        header: 'Node pools',
        enableSorting: false,
        cell: ({ row }) => muted(String(row.original.metadata?.pools?.length ?? 0)),
      },
      created: {
        id: 'created',
        header: 'Created',
        enableSorting: false,
        cell: ({ row }) => muted(formatDate(row.original.created_at)),
      },
    };

    const nameCol: ColumnDef<K8sCluster, unknown> = {
      id: 'name',
      header: 'Name',
      enableSorting: false,
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
          {row.original.name || row.original.id}
        </span>
      ),
    };

    const actionsCol: ColumnDef<K8sCluster, unknown> = {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <RowMenu
          items={[
            { label: 'Manage', onSelect: () => navigate(`${base}/${row.original.id}`) },
            { label: 'Delete', danger: true, onSelect: () => setDel(row.original) },
          ]}
        />
      ),
    };

    return [nameCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [navigate, visible, base]);

  const createBtn = (
    <Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/create`)}>
      Create cluster
    </Button>
  );

  return (
    <ListPageShell
      icon={<Boxes size={22} />}
      breadcrumbs={
        <Breadcrumbs>
          <BreadcrumbItem asChild><Link to="/services">Services</Link></BreadcrumbItem>
          <BreadcrumbItem current>Kubernetes clusters</BreadcrumbItem>
        </Breadcrumbs>
      }
      title={`Kubernetes clusters${clusters.length ? ` (${clusters.length})` : ''}`}
      description="Managed clusters with node pools, ingress and auto-scaling."
      actions={createBtn}
    >
      {isError ? (
        <ErrorState title="Couldn't load clusters" description="The cluster list is temporarily unavailable." onRetry={() => refetch()} />
      ) : isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          {/* Toolbar only renders when there is data to filter/search — avoids
              showing controls on a zero-state that already has a CTA. */}
          {clusters.length > 0 && (
            <Toolbar
              start={
                <Input
                  placeholder="Search clusters by name…"
                  startSlot={<Search />}
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                  style={{ maxWidth: 320 }}
                />
              }
              end={
                <>
                  <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="secondary" startIcon={<Filter size={15} />}>
                        Filters{conditions.length ? ` (${conditions.length})` : ''}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" style={{ padding: 0 }}>
                      <FilterBuilder
                        fields={filterFields}
                        defaultValue={conditions.length ? conditions : undefined}
                        onApply={(c) => {
                          // Drop blank conditions so an empty filter row isn't counted as active.
                          setConditions(c.filter((x) => x.value.trim()));
                          setPage(1);
                          setFilterOpen(false);
                        }}
                        onClose={() => setFilterOpen(false)}
                      />
                    </PopoverContent>
                  </Popover>
                  {conditions.length > 0 && (
                    <Button variant="ghost" onClick={() => { setConditions([]); setPage(1); }}>
                      Clear
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                        Display options
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                      {TOGGLEABLE.map((c) => (
                        <DropdownMenuCheckboxItem
                          key={c.id}
                          checked={visible[c.id]}
                          onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                          onSelect={(e) => e.preventDefault()}
                        >
                          {c.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              }
            />
          )}

          <DataTable
            columns={columns}
            data={pageRows}
            getRowId={(c) => String(c.id)}
            onRowClick={(c) => navigate(`${base}/${c.id}`)}
            emptyState={
              clusters.length === 0 ? (
                <EmptyState
                  icon={<Boxes size={20} />}
                  title="No clusters yet"
                  description="Create your first Kubernetes cluster."
                  actions={createBtn}
                />
              ) : (
                <EmptyState
                  icon={<Boxes size={20} />}
                  title="No matching clusters"
                  description="No clusters match your search or filters."
                />
              )
            }
            footer={total > 0 ? `Showing ${pageStart}–${pageEnd} of ${total}` : undefined}
          />

          {pageCount > 1 && (
            <div style={{ marginTop: 'var(--vn-space-4)' }}>
              <Pagination
                page={safePage}
                pageCount={pageCount}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete cluster?"
        description={`Permanently delete "${del?.name || del?.id}" and all its workloads?`}
        confirmLabel="Delete"
        destructive
        requireText={del?.name || del?.id}
        loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </ListPageShell>
  );
}
